import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircleOutline as PassIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  PlayArrow as ApproveIcon,
  InfoOutlined as InfoIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useSSEAwareQuery } from '../../hooks/useSSEAwareQuery';
import { useNavigate } from 'react-router-dom';
import {
  listScheduleRuns,
  listRunLogs,
  approveScheduleRun,
  ScheduleRun,
  PipelineRunLog,
} from '../../services/scheduleApi';
import { useNotifications } from '../../context/NotificationContext';
import ScheduleRunStepper from './ScheduleRunStepper';

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  completed: 'success',
  failed: 'error',
  skipped: 'warning',
  running: 'info',
  pending: 'default',
  pending_approval: 'warning',
};

const SEVERITY_COLORS: Record<string, string> = {
  info: '#2563eb',
  warn: '#d97706',
  error: '#dc2626',
};

function formatDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function duration(start?: string, end?: string): string {
  if (!start) return '-';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

interface ScheduleRunHistoryProps {
  scheduleId: string;
  projectId?: string;
}

export default function ScheduleRunHistory({ scheduleId, projectId }: ScheduleRunHistoryProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { scheduleProgress } = useNotifications();
  const { data: runs, isLoading } = useSSEAwareQuery(
    ['schedule-runs', scheduleId],
    () => listScheduleRuns(scheduleId),
    { pollingInterval: 10_000, ssePollingInterval: 30_000 },
  );
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  }, []);

  const handleApprove = useCallback(
    async (runId: string) => {
      try {
        await approveScheduleRun(scheduleId, runId);
        queryClient.invalidateQueries(['schedule-runs', scheduleId]);
      } catch {
        // Error handling via notification toast
      }
    },
    [scheduleId, queryClient],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={2}>
        No runs yet.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={32} />
            <TableCell>Started</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Phase</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Duration</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run: ScheduleRun) => {
            const metadata = (run.metadata || {}) as Record<string, any>;
            const hasValidation = !!metadata.validation_results;
            const hasDrift = !!metadata.drift_report;
            // All rows are expandable now (for execution logs)
            const isExpandable = true;
            const isExpanded = expandedRuns.has(run.run_id);

            // Match live SSE progress to this run
            const liveProgress =
              scheduleProgress?.scheduleId === scheduleId && scheduleProgress?.runId === run.run_id
                ? scheduleProgress
                : null;

            return (
              <React.Fragment key={run.run_id}>
                <TableRow
                  hover
                  onClick={() => toggleExpand(run.run_id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ px: 0.5 }}>
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      {isExpanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatDate(run.started_at || run.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={run.status === 'pending_approval' ? 'Awaiting Approval' : run.status}
                        size="small"
                        color={STATUS_COLORS[run.status] || 'default'}
                        variant="outlined"
                      />
                      {run.status === 'pending_approval' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon sx={{ fontSize: 12 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(run.run_id);
                          }}
                          sx={{ fontSize: 10, textTransform: 'none', px: 1, py: 0.25, minHeight: 0, boxShadow: 'none' }}
                        >
                          Approve &amp; Load
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{run.phase || '-'}</TableCell>
                  <TableCell>
                    {run.batch_id ? (
                      <Chip
                        label={run.batch_id.slice(0, 8)}
                        size="small"
                        variant="outlined"
                        clickable
                        icon={<OpenIcon sx={{ fontSize: 10 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/batches/${run.batch_id}/overview`);
                        }}
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {run.status === 'running'
                      ? duration(run.started_at || run.created_at)
                      : duration(run.started_at || run.created_at, run.completed_at)}
                  </TableCell>
                </TableRow>

                {/* Expanded details */}
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5, px: 2 }}>
                        {/* Phase stepper */}
                        <ScheduleRunStepper
                          currentPhase={run.phase}
                          runStatus={run.status}
                          liveProgress={liveProgress}
                        />

                        {/* Error message */}
                        {run.error_message && (
                          <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: alpha('#dc2626', 0.05), border: `1px solid ${alpha('#dc2626', 0.15)}` }}>
                            <Typography variant="caption" fontWeight={600} color="error" sx={{ fontSize: 11 }}>
                              Error
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: 12, mt: 0.5 }}>
                              {run.error_message}
                            </Typography>
                          </Box>
                        )}

                        {/* Schema drift report */}
                        {hasDrift && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, mb: 0.5, display: 'block' }}>
                              Schema Drift
                            </Typography>
                            <Box sx={{ p: 1, borderRadius: 1, bgcolor: alpha('#d97706', 0.05), border: `1px solid ${alpha('#d97706', 0.15)}` }}>
                              <Typography variant="caption" sx={{ fontSize: 11 }}>
                                Impact: <strong>{metadata.drift_report.impact || 'unknown'}</strong>
                              </Typography>
                              {metadata.drift_report.issues?.map((issue: any, idx: number) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <WarnIcon sx={{ fontSize: 12, color: '#d97706' }} />
                                  <Typography variant="caption" sx={{ fontSize: 10 }}>
                                    {issue.message || JSON.stringify(issue)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Validation results */}
                        {hasValidation && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, mb: 0.5, display: 'block' }}>
                              Export Validation
                            </Typography>
                            {metadata.validation_results.map((result: any, idx: number) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.75,
                                  py: 0.5, px: 1, mb: 0.25, borderRadius: 0.5,
                                  bgcolor: result.status === 'fail' ? alpha('#dc2626', 0.04) : result.status === 'warn' ? alpha('#d97706', 0.04) : alpha('#059669', 0.04),
                                }}
                              >
                                {result.status === 'pass' && <PassIcon sx={{ fontSize: 14, color: '#059669' }} />}
                                {result.status === 'warn' && <WarnIcon sx={{ fontSize: 14, color: '#d97706' }} />}
                                {result.status === 'fail' && <ErrorIcon sx={{ fontSize: 14, color: '#dc2626' }} />}
                                <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, flex: 1 }}>
                                  {result.table}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Execution logs (lazy-loaded) */}
                        <RunLogsSection scheduleId={scheduleId} runId={run.run_id} runStatus={run.status} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Lazy-loaded execution log timeline for a single run. */
function RunLogsSection({
  scheduleId,
  runId,
  runStatus,
}: {
  scheduleId: string;
  runId: string;
  runStatus: string;
}) {
  const { data: logs, isLoading } = useQuery(
    ['run-logs', runId],
    () => listRunLogs(scheduleId, runId),
    {
      refetchInterval: runStatus === 'running' ? 3000 : false,
    },
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={1}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        No execution logs available.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, mb: 0.5, display: 'block' }}>
        Execution Log
      </Typography>
      {logs.map((log: PipelineRunLog) => (
        <Box
          key={log.log_id}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            py: 0.4,
            px: 0.5,
            '&:hover': { bgcolor: 'action.hover', borderRadius: 0.5 },
          }}
        >
          {log.severity === 'error' ? (
            <ErrorIcon sx={{ fontSize: 13, color: SEVERITY_COLORS.error, mt: 0.2 }} />
          ) : log.severity === 'warn' ? (
            <WarnIcon sx={{ fontSize: 13, color: SEVERITY_COLORS.warn, mt: 0.2 }} />
          ) : (
            <InfoIcon sx={{ fontSize: 13, color: SEVERITY_COLORS.info, mt: 0.2 }} />
          )}
          <Chip
            label={log.phase}
            size="small"
            sx={{ fontSize: 9, height: 16, fontWeight: 600 }}
          />
          <Typography variant="caption" sx={{ fontSize: 11, flex: 1, color: log.severity === 'error' ? 'error.main' : 'text.primary' }}>
            {log.message}
          </Typography>
          {log.metrics && Object.keys(log.metrics).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.entries(log.metrics)
                .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
                .slice(0, 3)
                .map(([k, v]) => (
                  <Chip
                    key={k}
                    label={`${k}: ${v}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 9, height: 16 }}
                  />
                ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
